import axios, { AxiosError } from "axios";
import { createAppError } from "./createAppError";
import { getErrorMessage } from "./getErrorMessage";
import { isAppError } from "./isAppError";
import { normalizeServerError } from "./normalizeServerError";
import type { AppError } from "./types";

interface RtkQueryLikeError {
  status?: number;
  data?: unknown;
}

interface RouterErrorLike {
  status?: number;
  statusText?: string;
  data?: unknown;
}


/**
 * Checks if a value is a record (plain object).
 * Examples of records include: {}, { key: "value" }, but not arrays, functions, or null.
 * @param value unknown value to check
 * @returns boolean
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}



function isRtkQueryLikeError(error: unknown): error is RtkQueryLikeError {
  if (!isRecord(error)) {
    return false;
  }

  return "status" in error && ("data" in error || typeof error.status === "number");
}



function isRouterErrorLike(error: unknown): error is RouterErrorLike {
  if (!isRecord(error)) {
    return false;
  }

  return "status" in error && "statusText" in error;
}


/**
 * Extracts a message from an unknown error type.
 * @param error unknown error object
 * @returns string message
 */
function messageFromUnknown(error: unknown): string {
  
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  // default fallback message for unknown error types
  return getErrorMessage("unexpected");
}



/**
 * Normalizes an Axios error into a standardized AppError.
 * @param error AxiosError
 * @returns AppError
 */
function fromAxiosError(error: AxiosError): AppError {
  
  // Handle request cancellation
  if (error.code === "ERR_CANCELED") {
    return createAppError(
      "unexpected",
      "Request canceled",
      error,
      {
        code: error.code,
        defaultDisplayMode: "silent",
        retryable: false,
        logLevel: "warn",
      }
    );
  }

  // Handle timeout errors
  if (error.code === AxiosError.ECONNABORTED) {
    return createAppError(
      "timeout",
      error.message,
      error,
      {
        status: error.response?.status,
        code: error.code,
        defaultDisplayMode: "toast",
        retryable: true,
        logLevel: "warn",
      }
    );
  }

  // Handle network errors (no response received)
  if (!error.response) {
    return createAppError(
      "network",
      error.message,
      error,
      {
        code: error.code,
        defaultDisplayMode: "toast",
        retryable: true,
        logLevel: "warn",
      }
    );
  }

  return normalizeServerError(error.response.status, error.response.data, error.message, error);
}


/**
 * This function builds and returns AppError with appropriate properties set based on the error type.
 * Handling Axios, RTK Query-like, and router-like errors.
 * @param error unknown error object
 * @returns AppError
 */
export function normalizeError(error: unknown): AppError {

  if (isAppError(error)) {
    return error;
  }

  // Some error handlers wrap the original AppError in a plain Error (e.g. to satisfy
  // react-error-boundary's showBoundary), attaching it as `.appError`. Recover it so the
  // original kind/shouldLogout aren't lost when the wrapper is re-normalized upstream.
  if (isRecord(error) && isAppError((error as { appError?: unknown }).appError)) {
    return (error as { appError: AppError }).appError;
  }

  if (axios.isAxiosError(error)) {
    return fromAxiosError(error);
  }

  if (isRtkQueryLikeError(error)) {
    return normalizeServerError(error.status, error.data, messageFromUnknown(error), error);
  }

  if (isRouterErrorLike(error)) {
    const fallbackMessage = typeof error.statusText === "string" ? error.statusText : messageFromUnknown(error);
    return normalizeServerError(error.status, error.data, fallbackMessage, error);
  }

  if (error instanceof Error) {
    return createAppError(
      "unexpected",
      error.message,
      error,
      {
        defaultDisplayMode: "boundary",
        retryable: false,
        logLevel: "error",
      }
    );
  }

  return createAppError("unexpected", messageFromUnknown(error), error, {
    defaultDisplayMode: "boundary",
    retryable: false,
    logLevel: "error",
  });
}
